import React from 'react';
import {
    Box,
    Button,
    Popover,
    Checkbox,
    Chip,
    FormControlLabel,
    Typography,
    TextField,
    InputAdornment
} from '@mui/material';
import { FiChevronDown, FiSearch, FiX } from 'react-icons/fi';

const SEARCH_THRESHOLD = 8;
const SEM_GRUPO = '__sem_grupo__';

// options: [{ value, label, count?, group? }]
// Quando alguma opção tem `group` (ex.: cidade da filial), o popover ganha uma
// linha de chips para restringir por grupo e a lista fica seccionada, com
// "marcar todas" por seção. groupLabel é o nome dessa dimensão ("Cidade").
const FilterDropdown = ({ title, options, selected, onChange, groupLabel = 'Grupo' }) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [query, setQuery] = React.useState('');
    const [activeGroup, setActiveGroup] = React.useState(null);

    const safeOptions = Array.isArray(options) ? options : [];
    const safeSelected = Array.isArray(selected) ? selected : [];
    const activeCount = safeSelected.length;
    const isActive = activeCount > 0;

    const groups = React.useMemo(() => {
        const set = new Set();
        safeOptions.forEach((o) => { if (o.group) set.add(o.group); });
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }));
    }, [safeOptions]);
    const hasGroups = groups.length > 0;

    const filteredOptions = React.useMemo(() => {
        let list = safeOptions;
        if (activeGroup) list = list.filter((o) => (o.group || SEM_GRUPO) === activeGroup);
        if (query) {
            const q = query.toLowerCase();
            list = list.filter(
                (o) => o.label.toLowerCase().includes(q) || (o.group || '').toLowerCase().includes(q)
            );
        }
        return list;
    }, [safeOptions, query, activeGroup]);

    // Seções na ordem dos grupos; opções sem grupo vão para o fim.
    const sections = React.useMemo(() => {
        if (!hasGroups) return [{ key: SEM_GRUPO, title: null, items: filteredOptions }];
        const byGroup = new Map();
        filteredOptions.forEach((o) => {
            const key = o.group || SEM_GRUPO;
            if (!byGroup.has(key)) byGroup.set(key, []);
            byGroup.get(key).push(o);
        });
        const ordered = [...groups, SEM_GRUPO].filter((g) => byGroup.has(g));
        return ordered.map((g) => ({
            key: g,
            title: g === SEM_GRUPO ? 'Sem cidade' : g,
            items: byGroup.get(g)
        }));
    }, [filteredOptions, groups, hasGroups]);

    const handleToggle = (value) => {
        const exists = safeSelected.includes(value);
        onChange(exists ? safeSelected.filter((v) => v !== value) : [...safeSelected, value]);
    };

    const handleToggleSection = (items) => {
        const values = items.map((o) => o.value);
        const allChecked = values.every((v) => safeSelected.includes(v));
        if (allChecked) {
            onChange(safeSelected.filter((v) => !values.includes(v)));
        } else {
            onChange(Array.from(new Set([...safeSelected, ...values])));
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
        setQuery('');
        setActiveGroup(null);
    };

    const groupChipSx = (checked) => ({
        height: 22,
        fontSize: 11,
        fontWeight: 600,
        cursor: 'pointer',
        bgcolor: checked ? '#1a1a2e' : '#f0f1f5',
        color: checked ? '#fff' : '#1a1a2e',
        '&:hover': { bgcolor: checked ? '#2a2a3e' : '#e2e4ea' }
    });

    return (
        <>
            <Button
                onClick={(e) => setAnchorEl(e.currentTarget)}
                endIcon={<FiChevronDown size={14} />}
                disabled={safeOptions.length === 0 && !isActive}
                sx={{
                    flexShrink: 0,
                    textTransform: 'none',
                    fontSize: 13,
                    fontWeight: 600,
                    height: 34,
                    px: 1.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: isActive ? '#1a1a2e' : 'grey.300',
                    bgcolor: isActive ? '#1a1a2e' : '#fff',
                    color: isActive ? '#fff' : '#1a1a2e',
                    '&:hover': {
                        borderColor: '#1a1a2e',
                        bgcolor: isActive ? '#2a2a3e' : '#f0f1f5'
                    },
                    '&.Mui-disabled': { color: 'text.disabled', borderColor: 'grey.200' }
                }}
            >
                {title}
                {isActive && (
                    <Box
                        component="span"
                        sx={{
                            ml: 0.75,
                            px: 0.75,
                            minWidth: 18,
                            height: 18,
                            borderRadius: 9,
                            bgcolor: '#fff',
                            color: '#1a1a2e',
                            fontSize: 10,
                            fontWeight: 700,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        {activeCount}
                    </Box>
                )}
            </Button>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                    sx: {
                        mt: 0.75,
                        width: hasGroups ? 320 : 260,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.200',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                    }
                }}
            >
                <Box sx={{ p: 1.5 }}>
                    {safeOptions.length > SEARCH_THRESHOLD && (
                        <TextField
                            size="small"
                            fullWidth
                            autoFocus
                            placeholder="Buscar..."
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <FiSearch size={14} />
                                    </InputAdornment>
                                ),
                                sx: { fontSize: 12 }
                            }}
                            sx={{ mb: 1 }}
                        />
                    )}

                    {hasGroups && (
                        <Box sx={{ mb: 1 }}>
                            <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.secondary', mb: 0.5, textTransform: 'uppercase' }}>
                                {groupLabel}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                <Chip
                                    label="Todas"
                                    size="small"
                                    onClick={() => setActiveGroup(null)}
                                    sx={groupChipSx(activeGroup === null)}
                                />
                                {groups.map((g) => (
                                    <Chip
                                        key={g}
                                        label={g}
                                        size="small"
                                        onClick={() => setActiveGroup(activeGroup === g ? null : g)}
                                        sx={groupChipSx(activeGroup === g)}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}

                    {safeOptions.length === 0 ? (
                        <Typography sx={{ fontSize: 12, color: 'text.secondary', py: 1 }}>
                            Nenhuma opção disponível
                        </Typography>
                    ) : (
                        <Box
                            sx={{
                                maxHeight: 280,
                                overflowY: 'auto',
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            {sections.map((section) => {
                                const allChecked = section.items.every((o) => safeSelected.includes(o.value));
                                return (
                                    <Box key={section.key}>
                                        {section.title && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    mt: 0.75,
                                                    mb: 0.25,
                                                    pb: 0.25,
                                                    borderBottom: '1px solid',
                                                    borderColor: 'grey.200'
                                                }}
                                            >
                                                <Typography sx={{ fontSize: 11, fontWeight: 700, flex: 1, color: '#1a1a2e' }}>
                                                    {section.title}
                                                </Typography>
                                                <Box
                                                    component="button"
                                                    type="button"
                                                    onClick={() => handleToggleSection(section.items)}
                                                    sx={{
                                                        border: 'none',
                                                        bgcolor: 'transparent',
                                                        cursor: 'pointer',
                                                        fontSize: 10,
                                                        fontWeight: 600,
                                                        color: 'text.secondary',
                                                        p: 0,
                                                        '&:hover': { color: '#1a1a2e' }
                                                    }}
                                                >
                                                    {allChecked ? 'desmarcar todas' : 'marcar todas'}
                                                </Box>
                                            </Box>
                                        )}
                                        {section.items.map((opt) => (
                                            <FormControlLabel
                                                key={opt.value}
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={safeSelected.includes(opt.value)}
                                                        onChange={() => handleToggle(opt.value)}
                                                        sx={{ py: 0.25 }}
                                                    />
                                                }
                                                label={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                        <Typography sx={{ fontSize: 12, flex: 1 }}>
                                                            {opt.label}
                                                        </Typography>
                                                        {opt.count !== undefined && (
                                                            <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                                                                ({opt.count})
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                }
                                                sx={{ ml: 0, mr: 0, display: 'flex' }}
                                            />
                                        ))}
                                    </Box>
                                );
                            })}
                            {filteredOptions.length === 0 && (
                                <Typography sx={{ fontSize: 11, color: 'text.secondary', py: 0.5 }}>
                                    {query ? `Nenhum resultado para "${query}"` : 'Nenhuma opção nesse grupo'}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {isActive && (
                        <Button
                            fullWidth
                            size="small"
                            onClick={() => onChange([])}
                            startIcon={<FiX size={12} />}
                            sx={{
                                mt: 1,
                                textTransform: 'none',
                                fontSize: 11,
                                fontWeight: 600,
                                color: '#1a1a2e'
                            }}
                        >
                            Limpar {title}
                        </Button>
                    )}
                </Box>
            </Popover>
        </>
    );
};

export default FilterDropdown;
